/** @file ExportMenu: the top bar's Export menu — runtime bundle or baked sprite sheets, with bake options. */
import { useState } from 'react';
import type { ReactElement } from 'react';
import type { ExportQuery } from '@zilo/pose3d-api/types/restAdded';
import { Button } from '../ui-kit/Button';
import { SettingNumber } from '../ui-kit/SettingNumber';
import { Select } from '../ui-kit/Select';
import type { BakeForm } from './bakedQuery';
import { bakedQuery } from './bakedQuery';
import { EDITOR_SETTINGS } from './editorSettings';

/** The menu's state: closed, or open under the button at `left`/`top`. */
type Place = { left: number; top: number } | null;

const DIRECTIONS = [
  { value: '', label: "Puppet's facings" }, { value: '1', label: '1' }, { value: '2', label: '2' }, { value: '4', label: '4' },
  { value: '8', label: '8' }, { value: '16', label: '16' },
];
const GRID = [{ value: '', label: 'Grid: profile default' }, { value: 'on', label: 'Grid sheet: on' }, { value: 'off', label: 'Grid sheet: off' }];

/** The baked half of the popover: directions, scale, trim, grid, and the download button. */
function BakeOptions({ form, setForm, onBake }: { form: BakeForm; setForm: (f: BakeForm) => void; onBake: () => void }): ReactElement {
  return (
    <div className="p3-field" role="group" aria-label="Bake options" style={{ padding: '4px 10px 6px' }}>
      <span title="Every clip x direction x frame rendered into sprite sheets with baked.json and engine importers (Phaser, Godot, Unity, Aseprite); no runtime needed, bigger files">
        Baked sprite sheets: any engine, no runtime
      </span>
      <label className="p3-field p3-field-row">Directions
        <Select label="Directions" value={form.directions} options={DIRECTIONS} onChange={(directions) => { setForm({ ...form, directions }); }} />
      </label>
      <label className="p3-field p3-field-row">Scale
        <SettingNumber label="Scale" value={form.scale} min={1} max={8} integer onCommit={(scale) => { setForm({ ...form, scale }); }} />
      </label>
      <label className="p3-field p3-field-row">Trim cells
        <input type="checkbox" aria-label="Trim cells" checked={form.trim} onChange={(e) => { setForm({ ...form, trim: e.target.checked }); }} />
      </label>
      <Select label="Grid sheet" value={form.grid} options={GRID} onChange={(grid) => { setForm({ ...form, grid }); }} />
      <button type="button" role="menuitem" className="p3-menu-item" onClick={onBake}>Baked sprite sheets (zip)</button>
    </div>
  );
}

/**
 * "Export ▾" opens a popover under the button: "Runtime bundle (zip)" (the Pose3D runtime: live camera, IK, small
 * files) and "Baked sprite sheets (zip)" with a few bake options. Picking either calls `onExport` (undefined = the
 * bundle) and closes; a click outside closes it.
 */
export function ExportMenu({ disabled, onExport }: { disabled: boolean; onExport: (query?: ExportQuery) => void }): ReactElement {
  const [place, setPlace] = useState<Place>(null);
  const [form, setForm] = useState<BakeForm>(() => { const d = EDITOR_SETTINGS.export.load(); return { directions: d.directions, scale: d.scale, trim: true, grid: '' }; });
  const open = (r: DOMRect): void => { setPlace({ left: Math.max(0, r.right - 240), top: r.bottom }); };
  const pick = (query?: ExportQuery): void => { setPlace(null); onExport(query); };
  return (
    <>
      <Button onClick={(e) => { open(e.currentTarget.getBoundingClientRect()); }} disabled={disabled} aria-haspopup="menu" aria-expanded={place !== null} title="Export this puppet">Export ▾</Button>
      {place === null ? null : (
        <>
          <div className="p3-menu-backdrop" data-popover-backdrop data-testid="export-backdrop" onClick={() => { setPlace(null); }} />
          <div className="p3-menu" role="menu" aria-label="Export" style={{ ...place, width: 240 }}>
            <button type="button" role="menuitem" className="p3-menu-item" title="For the Pose3D runtime: live camera, IK, smaller files" onClick={() => { pick(); }}>
              Runtime bundle (zip)
            </button>
            <div className="p3-menu-sep" role="separator" />
            <BakeOptions form={form} setForm={setForm} onBake={() => { pick(bakedQuery(form)); }} />
          </div>
        </>
      )}
    </>
  );
}
